/**
 * CalendarEventDisplay - Unified component for displaying calendar events
 * 
 * Supports both single event (nextCalendarEvent) and multiple events (calendarEvents)
 * with various display variants for different contexts (tables, cards, detail pages).
 * 
 * Features:
 * - Clickable date link to calendar detail page
 * - Time display (handles "Upon Adjournment")
 * - Location/room number
 * - Add to Calendar dropdown
 * - "+N more" indicator for lists
 * 
 * @module components/shared/CalendarEventDisplay
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, CalendarPlus } from 'lucide-react';
import AddToCalendarDropdown from '../calendar/AddToCalendarDropdown';
import { NextCalendarEvent } from '../../types/bill';
import { formatDate } from '../../utils/dateUtils';

// Normalized event type for internal use
interface NormalizedEvent {
  calendar_id: number;
  reading_date: string;
  reading_time?: string;
  reading_title: string;
  room_number?: string;
  derived_location?: string;
  chamber?: string;
  upon_adjournment?: boolean;
}

// Normalize various event formats to a common structure
const normalizeEvent = (e: any): NormalizedEvent | null => {
  if (!e) return null;
  
  // Get the date from various possible property names
  const date = e.reading_date || e.date || e.event_date;
  if (!date) return null;
  
  return {
    calendar_id: parseInt(e.calendar_id) || 0,
    reading_date: date,
    reading_time: e.reading_time || e.time,
    reading_title: e.reading_title || e.description || e.event_type || e.title || 'Scheduled',
    room_number: e.room_number || e.location,
    derived_location: e.derived_location,
    chamber: e.chamber,
    upon_adjournment: e.upon_adjournment,
  };
};

export interface CalendarEventDisplayProps {
  /** Single event (for next calendar event display) */
  event?: NextCalendarEvent | null;
  /** Multiple events (for hot bills reports) - accepts various event formats */
  events?: any[];
  /** Bill number for calendar export (optional, derives from event if not provided) */
  billNumber?: string;
  /** Bill title for calendar export (optional, derives from event if not provided) */
  billTitle?: string;
  /** Display variant */
  variant?: 'compact' | 'inline' | 'block' | 'list';
  /** Context for navigation routes */
  context?: 'authenticated' | 'public';
  /** Use public routes (for unauthenticated pages) - deprecated, use context instead */
  isPublic?: boolean;
  /** Show the calendar icon */
  showIcon?: boolean;
  /** Show the "Scheduled:" label */
  showLabel?: boolean;
  /** Max events to display (for list variant) */
  maxDisplay?: number;
  /** Show "+N more" indicator */
  showMoreCount?: boolean;
  /** Show Add to Calendar button */
  showAddToCalendar?: boolean;
  /** Show event title/description */
  showTitle?: boolean;
  /** Empty state text */
  emptyText?: string;
  /** Additional className for container */
  className?: string;
}

/**
 * Unified component for displaying calendar events with:
 * - Single or multiple event support
 * - Clickable date link (to calendar detail or public event page)
 * - Time display (handles "Upon Adjournment")
 * - Location/room number
 * - Add to Calendar dropdown (+)
 * - "+N more" indicator for lists
 * 
 * Variants:
 * - compact: Date/time/location stacked with + button (for table cells)
 * - inline: Single line with all info (for meta sections)
 * - block: Stacked layout (for hero cards)
 * - list: Multiple events with titles (for hot bills)
 */
const CalendarEventDisplay: React.FC<CalendarEventDisplayProps> = ({
  event,
  events,
  billNumber = '',
  billTitle = '',
  variant = 'inline',
  context,
  isPublic = false,
  showIcon = true,
  showLabel = true,
  maxDisplay = 2,
  showMoreCount = true,
  showAddToCalendar = true,
  showTitle = false,
  emptyText = '-',
  className = '',
}) => {
  // Determine if we're in a public context (context prop takes precedence over isPublic)
  const isPublicContext = context === 'public' || isPublic;
  // Normalize events to common format and filter to upcoming only
  const allEvents = events || (event ? [event] : []);
  const normalizedEvents = allEvents
    .map(normalizeEvent)
    .filter((e): e is NormalizedEvent => e !== null);
  
  // Parse calendar date as local day: strip any time part (API may send "2026-02-09" or "2026-02-09T00:00:00.000Z")
  // so we always show the correct calendar day regardless of timezone
  const parseCalendarDate = (dateStr: string): Date => {
    const dateOnly = (dateStr || '').split('T')[0];
    return new Date(dateOnly ? dateOnly + 'T00:00:00' : dateStr);
  };

  const upcomingEvents = normalizedEvents.filter(
    e => parseCalendarDate(e.reading_date) >= new Date(new Date().toDateString())
  );
  
  if (upcomingEvents.length === 0) {
    return (
      <span className={`text-sm text-text-tertiary dark:text-text-dark-tertiary ${className}`}>
        {emptyText}
      </span>
    );
  }

  const nextEvent = upcomingEvents[0];


  // Format time for display
  const formatTime = (e: NormalizedEvent) => {
    if (e.upon_adjournment) return 'Upon Adjournment';
    if (!e.reading_time) return null;
    const [hours, minutes] = e.reading_time.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  // Get link route
  const getLink = (e: NormalizedEvent) => isPublicContext 
    ? `/public/calendar/events/${e.calendar_id}`
    : `/calendar/${e.reading_date}`;

  // Add to Calendar dropdown - wrapped to prevent event bubbling
  const renderAddToCalendar = (e: NormalizedEvent) => showAddToCalendar && (
    <div onClick={(ev) => ev.stopPropagation()}>
      <AddToCalendarDropdown
        event={{
          eventId: e.calendar_id,
          title: e.reading_title,
          date: e.reading_date,
          time: e.reading_time,
          location: e.room_number,
          billNumber,
          billTitle,
          uponAdjournment: e.upon_adjournment
        }}
        size="sm"
      />
    </div>
  );

  // List variant - multiple events
  if (variant === 'list') {
    const displayEvents = upcomingEvents.slice(0, maxDisplay);
    const remainingCount = upcomingEvents.length - maxDisplay;

    return (
      <div className={`space-y-1.5 ${className}`}>
        {displayEvents.map((e, idx) => {
          const time = formatTime(e);
          return (
            <div key={e.calendar_id || idx} className="flex items-start gap-2">
              {showIcon && idx === 0 && (
                <Calendar className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary mt-0.5 flex-shrink-0" />
              )}
              {showIcon && idx > 0 && <div className="w-4" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Link 
                    to={getLink(e)} 
                    onClick={(ev) => ev.stopPropagation()}
                    className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    {formatDate(e.reading_date, { year: undefined })}
                  </Link>
                  {time && (
                    <span className="text-xs text-text-secondary dark:text-text-dark-secondary">
                      {time}
                    </span>
                  )}
                  {renderAddToCalendar(e)}
                </div>
                {showTitle && e.reading_title && (
                  <div className="text-xs text-text-secondary dark:text-text-dark-secondary line-clamp-1">
                    {e.reading_title}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {showMoreCount && remainingCount > 0 && (
          <div className="text-xs text-primary-600 dark:text-primary-400 font-medium ml-6">
            +{remainingCount} more event{remainingCount > 1 ? 's' : ''}
          </div>
        )}
      </div>
    );
  }

  // Single event variants (compact/inline/block)
  const time = formatTime(nextEvent);

  // Compact variant - for table cells
  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="flex flex-col">
          <Link
            to={getLink(nextEvent)}
            onClick={(e) => e.stopPropagation()}
            className="text-sm text-primary-600 dark:text-primary-400 hover:underline font-medium"
          >
            {formatDate(nextEvent.reading_date)}
          </Link>
          {time && (
            <span className="text-xs text-text-secondary dark:text-text-dark-secondary">
              {time}
            </span>
          )}
          {(nextEvent.room_number || nextEvent.derived_location) && (
            <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
              {nextEvent.room_number ? `Room ${nextEvent.room_number}` : nextEvent.derived_location}
            </span>
          )}
        </div>
        {renderAddToCalendar(nextEvent)}
      </div>
    );
  }

  // Inline variant - single line for meta sections
  if (variant === 'inline') {
    return (
      <div className={`flex items-center gap-2 flex-wrap ${className}`}>
        {showIcon && (
          <CalendarPlus className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary flex-shrink-0" />
        )}
        {showLabel && (
          <span className="text-text-secondary dark:text-text-dark-secondary">Scheduled:</span>
        )}
        <Link
          to={getLink(nextEvent)}
          onClick={(e) => e.stopPropagation()}
          className="font-medium text-primary-600 dark:text-primary-400 hover:underline"
        >
          {formatDate(nextEvent.reading_date)}
        </Link>
        {time && (
          <span className="text-text-secondary dark:text-text-dark-secondary">
            at {time}
          </span>
        )}
        {(nextEvent.room_number || nextEvent.derived_location) && (
          <span className="text-text-tertiary dark:text-text-dark-tertiary">
            • {nextEvent.room_number ? `Room ${nextEvent.room_number}` : nextEvent.derived_location}
          </span>
        )}
        {renderAddToCalendar(nextEvent)}
      </div>
    );
  }

  // Block variant - stacked for hero cards
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div className="flex flex-col">
        <Link
          to={getLink(nextEvent)}
          onClick={(e) => e.stopPropagation()}
          className="text-primary-600 dark:text-primary-400 hover:underline font-medium"
        >
          {formatDate(nextEvent.reading_date)}
        </Link>
        {time && (
          <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
            {time}
          </span>
        )}
        {(nextEvent.room_number || nextEvent.derived_location) && (
          <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
            {nextEvent.room_number ? `Room ${nextEvent.room_number}` : nextEvent.derived_location}
          </span>
        )}
      </div>
      {renderAddToCalendar(nextEvent)}
    </div>
  );
};

export default CalendarEventDisplay;
